// Ported from files/snapdraw-design-kit/code/src/components/snapdraw/form.tsx
// (Lovable reference implementation). FieldError is new in the 2026-08-22
// "form validation & error states" kit update — added an optional `id` prop
// (the kit's own version doesn't take one) so callers can wire it to an
// input's `aria-describedby` properly.
import { useState } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { AlertCircle } from "lucide-react";

import { cn } from "@/lib/utils";

/** Titled panel used down the left rail of the document builders. */
export function Panel({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("sd-card p-5", className)}>
      <h2 className="font-display text-xl tracking-wide text-foreground">{title}</h2>
      <div className="mt-4 border-t border-border pt-4 space-y-4">{children}</div>
    </section>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="sd-label">{label}</span>
      <span className="mt-2 block">{children}</span>
    </label>
  );
}

/** Inline validation message used below inputs. */
export function FieldError({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <p
      id={id}
      className="flex items-start gap-2 rounded-lg border-l-2 border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
      role="alert"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

export function TextInput({
  className,
  numeric,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { numeric?: boolean }) {
  return (
    <input
      {...props}
      className={cn(
        "h-11 w-full rounded-lg border border-border bg-black/30 px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-arc",
        numeric && "sd-numeric",
        className,
      )}
    />
  );
}

/**
 * A dollar-denominated numeric input — fixed `$` prefix, right-aligned value.
 * Used for the Quote line-item Rate field (DESIGN-SPEC.md §12.8's "labeled
 * line grid" rule) so a bare number isn't mistaken for anything else.
 */
export function CurrencyInput({
  value,
  onChange,
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> & {
  value: number | string;
  onChange?: (value: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  const raw = typeof value === "number" ? String(value) : value;
  const parsed = Number(raw);
  // While typing the field shows exactly what was typed; at rest it settles to
  // two decimals so the rate column reads as money.
  const stringValue = !focused && raw.trim() !== "" && !Number.isNaN(parsed) ? parsed.toFixed(2) : raw;
  return (
    <div className={cn("relative", className)}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
      <input
        {...props}
        type="text"
        inputMode="decimal"
        value={stringValue}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={(e) => {
          setFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          props.onBlur?.(e);
        }}
        className="h-11 w-full rounded-lg border border-border bg-black/30 py-0 pl-6 pr-3 text-right text-sm text-foreground placeholder:text-muted-foreground focus:border-arc sd-numeric"
      />
    </div>
  );
}

export function TextArea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        "w-full rounded-lg border border-border bg-black/30 px-3 py-2.5 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-arc",
        className,
      )}
    />
  );
}

export function CheckboxRow({
  label,
  checked,
  defaultChecked,
  onChange,
}: {
  label: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-foreground/80">
      <input
        type="checkbox"
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={(e) => onChange?.(e.target.checked)}
        className="h-4 w-4 rounded border-border-strong bg-black/40 accent-[var(--sd-arc)]"
      />
      {label}
    </label>
  );
}

export function TotalRow({
  label,
  value,
  emphasis,
  note,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  /** Small caption under the row — used to explain how the row was derived. */
  note?: string;
}) {
  if (emphasis) {
    return (
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3 border-t border-border-strong pt-5">
        <h3 className="font-display text-3xl tracking-wide text-foreground lg:text-4xl">{label}</h3>
        <span className="sd-numeric text-3xl font-bold text-arc lg:text-4xl">{value}</span>
      </div>
    );
  }
  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-4 text-sm">
        <span className="text-foreground/80">{label}</span>
        <span className="sd-numeric text-foreground">{value}</span>
      </div>
      {note ? (
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{note}</p>
      ) : null}
    </div>
  );
}

export function PrimaryButton({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        // Real-device review (2026-08-31): disabled:opacity-50 alone fades
        // the arc gradient toward the page background — reads as "dimmed"
        // in dark mode (fading toward black), but as a soft pastel active
        // button in light mode (fading toward white). Explicit neutral
        // fill + muted text instead, in both themes.
        "inline-flex h-12 items-center justify-center rounded-xl bg-[image:var(--gradient-arc)] px-6 font-display text-lg uppercase tracking-wide text-primary-foreground transition-shadow hover:glow-arc disabled:cursor-not-allowed disabled:bg-none disabled:bg-surface-raised disabled:text-muted-foreground",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex h-12 items-center justify-center rounded-xl border border-border-strong px-6 font-display text-lg uppercase tracking-wide text-foreground hover:border-arc hover:text-arc disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
}
